import { NFTI } from '@tacans/shared-marketplace-layer';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useCallback, useMemo, useState } from 'react';
import PageMeta from 'src/components/organisms/page-meta';
import { PAGES_LOCALES } from 'src/constants/common.constants';
import { fetchCollectionsByIdsThunk } from 'src/features/common/collection/ducks/list/list.thunk';
import { ExploreContentLayout } from 'src/features/common/explore/components/explore-content-layout';
import { ExploreHeader } from 'src/features/common/explore/components/explore-header';
import { FilterButton } from 'src/features/common/explore/components/filter-button';
import { ExploreFilterContainer } from 'src/features/common/explore/containers/explore-filter-container';
import { ExploreTopFiltersContainer } from 'src/features/common/explore/containers/explore-top-filters-container';
import { NftsExploreContainer } from 'src/features/common/explore/containers/nfts-explore-container';
import { fetchNftsThunk } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.thunk';
import {
    useClearFilters,
    useFilters,
    useIsFiltersApplied,
    useTotal,
} from 'src/features/common/explore/hooks/use-explore-nfts';
import { EmptyListPlaceholder } from 'src/features/common/shared/components/empty-list-placeholder';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { fetchUsersByWalletsThunk } from 'src/features/common/user/ducks/user-list';
import { ExplorePageWhiteLabel } from 'src/features/white-labels/ocean/explore/components/explore-page';
import { StyledAppContainer } from 'src/features/white-labels/techcontemporary/app/components/app-container/app-container.styles';
import { TechExploreContentLayout } from 'src/features/white-labels/techcontemporary/explore/components/explore-content-layout';
import { ExploreHeader as TechExploreHeader } from 'src/features/white-labels/techcontemporary/explore/components/explore-header';
import { ArtsExploreContainer } from 'src/features/white-labels/techcontemporary/explore/containers/arts-explore-container';
import { TechExploreFilterContainer } from 'src/features/white-labels/techcontemporary/explore/containers/explore-filter-container';
import { ExploreTopFiltersContainer as TechExploreTopFiltersContainer } from 'src/features/white-labels/techcontemporary/explore/containers/explore-top-filters-container';
import { fetchArtsThunk } from 'src/features/white-labels/techcontemporary/explore/ducks/explore-arts/explore-arts.thunk';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useMeta } from 'src/hooks/use-meta';
import { wrapper } from 'src/store';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { PageLayout } from 'src/ui/common/layouts/page-layout';
import { SimpleTabs } from 'src/ui/common/molecules/simple-tabs';

const ExplorePage = () => {
    const total = useTotal();
    const { keywords } = useFilters();
    const isFiltersApplied = useIsFiltersApplied('explore');
    const clearFilters = useClearFilters();
    const [sideBarOpened, setSideBarOpened] = useState(true);
    const { isOceanTemplate, isTechContemporary } = useGetUniverseTemplate();
    const { t } = useTranslation(['pages', 'components']);

    const { projectName } = useUniverse();
    const meta = useMeta('explore', {
        projectName,
    });

    const tabs = useMemo(() => [{ tabId: 'items', tab: total ? `Items (${total})` : 'Items' }], [total]);

    const toggleSideBar = useCallback(() => setSideBarOpened((s) => !s), []);

    const emptyList = useMemo(
        () => ({
            title: t('emptyList.nft.title', { ns: 'components' }),
            description: t(`emptyList.nft.${isFiltersApplied || !!keywords ? 'textSearch' : 'text'}`, {
                ns: 'components',
            }),
        }),
        [t, isFiltersApplied, keywords]
    );

    const emptyPlaceholder = useCallback(
        ({ itemsGridConfig }) => (
            <EmptyListPlaceholder itemsGridConfig={itemsGridConfig}>
                <Typography size="base" tag="div" variant="title" color="text">
                    {isTechContemporary ? 'No ARTs found' : emptyList.title}
                </Typography>
                <Spacing margin="10px 0 30px">
                    <Typography size="sm" tag="div" color="gs200">
                        <span dangerouslySetInnerHTML={{ __html: emptyList.description }} />
                    </Typography>
                </Spacing>
            </EmptyListPlaceholder>
        ),
        [emptyList]
    );

    if (isOceanTemplate) {
        return (
            <ExplorePageWhiteLabel
                title={meta.title}
                description={meta.description}
                sideBarOpened={sideBarOpened}
                toggleSideBar={toggleSideBar}
                isFiltersApplied={isFiltersApplied}
                clearFilters={clearFilters}
                tabItems={tabs}
                emptyList={emptyList}
            />
        );
    }

    if (isTechContemporary) {
        return (
            <>
                <PageMeta title="Explore ARTs" description={meta.description} />
                <StyledAppContainer>
                    <TechExploreHeader title={t('explore.title', { ns: 'tech-contemporary' })}>
                        <Spacing mt="8px">
                            <Typography color="gs100">{t('explore.subTitle', { ns: 'tech-contemporary' })}</Typography>
                        </Spacing>
                    </TechExploreHeader>
                    <TechExploreContentLayout.Content>
                        <TechExploreContentLayout.SideBar opened={sideBarOpened}>
                            <TechExploreFilterContainer />
                        </TechExploreContentLayout.SideBar>
                        <TechExploreContentLayout.ExploreContent>
                            <TechExploreTopFiltersContainer />
                            <ArtsExploreContainer emptyPlaceholder={emptyPlaceholder} />
                        </TechExploreContentLayout.ExploreContent>
                    </TechExploreContentLayout.Content>
                </StyledAppContainer>
            </>
        );
    }

    return (
        <>
            <PageMeta title={meta.title} description={meta.description} />
            <PageLayout fullWidth>
                <ExploreHeader title={t('explore.title', { ns: 'pages' })} />
                <SimpleTabs initialTabId="items">
                    <ExploreContentLayout>
                        <ExploreContentLayout.TopPanel>
                            <FilterButton
                                opened={sideBarOpened}
                                onToggle={toggleSideBar}
                                showClearBtn={isFiltersApplied}
                                onClear={clearFilters}
                            />
                            <SimpleTabs.TabItems tabItems={tabs} />
                        </ExploreContentLayout.TopPanel>
                        <ExploreContentLayout.Content>
                            <ExploreContentLayout.SideBar opened={sideBarOpened}>
                                <ExploreFilterContainer />
                            </ExploreContentLayout.SideBar>
                            <ExploreContentLayout.ExploreContent>
                                <SimpleTabs.Content tabId="items">
                                    <ExploreTopFiltersContainer />
                                    <NftsExploreContainer
                                        sideBarOpened={sideBarOpened}
                                        emptyPlaceholder={emptyPlaceholder}
                                    />
                                </SimpleTabs.Content>
                            </ExploreContentLayout.ExploreContent>
                        </ExploreContentLayout.Content>
                    </ExploreContentLayout>
                </SimpleTabs>
            </PageLayout>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps((store) => async (ctx) => {
    const [data] = await Promise.all([
        // @ts-ignore
        store.dispatch(fetchNftsThunk()),
        // @ts-ignore
        store.dispatch(fetchArtsThunk()),
    ]);

    if (data?.payload?.list?.length) {
        // @ts-ignore
        await store.dispatch(
            // @ts-ignore
            fetchUsersByWalletsThunk(
                // @ts-ignore
                data.payload.list.map((item) => ({
                    address: item.author,
                    blockchainId: item.blockchainId,
                }))
            )
        );
    }
    if (data?.payload?.list?.length) {
        const collectionsIds = data.payload.list.map((item: NFTI) => item.collectionId).filter(Boolean);
        if (collectionsIds.length) {
            // @ts-ignore
            await store.dispatch(fetchCollectionsByIdsThunk(collectionsIds));
        }
    }

    return {
        props: {
            ...(await serverSideTranslations(ctx.locale as string, PAGES_LOCALES)),
        },
    };
});

export default ExplorePage;